import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  CurrencyInputV2,
  DocumentNumberFieldV2,
  FormFieldV2,
  NumericInputV2,
  SearchFieldV2 as SearchField,
  AritoIcon
} from '@/components/custom/erp';
import {
  accountSearchColumns,
  hanThanhToanSearchColumns,
  khachHangSearchColumns,
  MA_CHUNG_TU,
  nhanVienSearchColumns,
  QUERY_KEYS
} from '@/constants';
import { AccountModel, HanThanhToan, KhachHang, NhanVien } from '@/types/schemas';
import { useHoaDonMuaHangTrongNuocSearchContext } from '../../context';
import { useDetailSelection } from '@/hooks/use-detail-selection';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

const tabsMini = [
  { id: 0, title: 'Chứng từ mua' },
  { id: 1, title: 'Hóa đơn' },
  { id: 2, title: 'Phiếu nhập' }
];

const topCheckboxClassName = 'flex h-8 items-center gap-2';
const topSelectClassName =
  'h-8 rounded-none border-0 border-b border-b-gray-200 bg-transparent px-2 text-[13px] font-semibold text-black outline-none focus:border-b-blue-600 disabled:opacity-100';
const topNumberInputClassName =
  'h-8 rounded-none border-0 border-b border-b-gray-200 bg-transparent px-2 text-right text-[13px] font-semibold text-black outline-none focus:border-b-blue-600 disabled:opacity-100';

export function BasicInfoTab({ formMode, formState: { state, actions } }: BasicInfoTabProps) {
  const isViewMode = formMode === 'view';
  const [selectedTabId, setSelectedTabId] = useState<number>(0);
  const { setValue } = useFormContext();
  const prevDienGiaiStateRef = useRef<string>('');
  const { setNgayCt } = useHoaDonMuaHangTrongNuocSearchContext();
  const handleAccountSelection = useDetailSelection<AccountModel>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    onSelect: actions.setTaiKhoan
  });
  const pc_tao_yn = state.pc_tao_yn;
  const ck_yn = state.ck_yn;
  const loai_ck = state.loai_ck;
  const pn_yn = state.pn_yn;

  const ngay_ct = useWatch({ name: 'ngay_ct' });
  const dien_giai = useWatch({ name: 'dien_giai' });

  useEffect(() => {
    if (ngay_ct && formMode !== 'view') {
      setValue('ngay_pn', ngay_ct);
      setValue('ngay_ct0', ngay_ct);
      setNgayCt(ngay_ct);
    }
  }, [formMode, ngay_ct, setNgayCt, setValue]);

  useEffect(() => {
    const customerUuid = state.khachHang?.uuid || '';
    setValue('ma_kh', customerUuid, { shouldDirty: false, shouldValidate: false });
  }, [setValue, state.khachHang?.uuid]);

  useEffect(() => {
    if (formMode === 'view') return;

    const isNewDienGiai = state.dienGiai && state.dienGiai !== prevDienGiaiStateRef.current;
    const isFormFieldEmpty = !dien_giai || dien_giai.trim() === '';

    if (isNewDienGiai && (isFormFieldEmpty || state.dienGiai !== dien_giai)) {
      setValue('dien_giai', state.dienGiai, { shouldDirty: true });
    }

    if (state.dienGiai) {
      prevDienGiaiStateRef.current = state.dienGiai;
    }
  }, [dien_giai, formMode, setValue, state.dienGiai]);

  const availableTabs = useMemo(() => tabsMini.filter(tab => (tab.id === 2 ? pn_yn : true)), [pn_yn]);

  useEffect(() => {
    const isCurrentTabAvailable = availableTabs.some(tab => tab.id === selectedTabId);
    if (!isCurrentTabAvailable) {
      setSelectedTabId(0);
    }
  }, [availableTabs, selectedTabId]);

  const rowClassName = 'flex items-center gap-2 xl:gap-3';
  const leftLabelClassName = 'w-[180px] shrink-0 text-[13px] xl:w-[195px]';
  const middleLabelClassName = 'w-[120px] shrink-0 text-[13px] xl:w-[132px]';
  const rightLabelClassName = 'w-[104px] shrink-0 text-[13px] xl:w-[112px] 2xl:w-[127px]';

  return (
    <div className='overflow-x-auto'>
      <div className='min-w-[1460px] px-3 pb-2 pt-1.5 xl:min-w-0 xl:px-4 xl:pb-3 xl:pt-2'>
        <div className='mb-1.5 flex min-h-8 items-center gap-7'>
          <div className={topCheckboxClassName}>
            <Checkbox
              name='hdmh_yn'
              id='hdmh_yn'
              checked={state.hdmh_yn}
              onCheckedChange={checked => {
                setValue('hdmh_yn', !!checked);
                actions.setHdmhYn(!!checked);
              }}
              disabled={isViewMode}
            />
            <Label htmlFor='hdmh_yn' className='whitespace-nowrap text-[14px]'>
              Hóa đơn
            </Label>
          </div>

          <div className={topCheckboxClassName}>
            <Checkbox
              name='pn_yn'
              id='pn_yn'
              checked={state.pn_yn}
              onCheckedChange={checked => {
                setValue('pn_yn', !!checked);
                actions.setPnYn(!!checked);
              }}
              disabled={isViewMode}
            />
            <Label htmlFor='pn_yn' className='whitespace-nowrap text-[14px]'>
              Nhập kho
            </Label>
          </div>

          <div className={topCheckboxClassName}>
            <Checkbox
              name='pc_tao_yn'
              id='pc_tao_yn'
              checked={pc_tao_yn}
              onCheckedChange={checked => {
                setValue('pc_tao_yn', !!checked);
                actions.setPcTaoYn(!!checked);
              }}
              disabled={isViewMode}
            />
            <Label htmlFor='pc_tao_yn' className={pc_tao_yn ? 'sr-only' : 'whitespace-nowrap text-[14px]'}>
              Chi tiền
            </Label>
            <select
              name='ma_httt'
              value={state.ma_httt}
              disabled={isViewMode || !pc_tao_yn}
              aria-hidden={!pc_tao_yn}
              tabIndex={pc_tao_yn ? 0 : -1}
              onChange={event => {
                setValue('ma_httt', event.target.value);
                actions.setMaHttt(event.target.value);
              }}
              className={`${topSelectClassName} w-[150px] ${pc_tao_yn ? '' : 'hidden'}`}
            >
              <option value='TMM'>Tiền mặt</option>
              <option value='CKM'>Chuyển khoản</option>
              <option value='KM'>Khác</option>
            </select>
          </div>

          <div className={topCheckboxClassName}>
            <Checkbox
              name='xt_yn'
              id='xt_yn'
              disabled={isViewMode}
              checked={state.xt_yn}
              onCheckedChange={checked => {
                setValue('xt_yn', !!checked);
                actions.setXtYn(!!checked);
              }}
            />
            <Label htmlFor='xt_yn' className='whitespace-nowrap text-[14px]'>
              Xuất thẳng
            </Label>
          </div>

          <div className={topCheckboxClassName}>
            <Checkbox
              name='ck_yn'
              id='ck_yn'
              checked={ck_yn}
              onCheckedChange={checked => {
                setValue('ck_yn', !!checked);
                actions.setCkYn(!!checked);
              }}
              disabled={isViewMode}
            />
            <Label htmlFor='ck_yn' className={ck_yn ? 'sr-only' : 'whitespace-nowrap text-[14px]'}>
              Chiết khấu
            </Label>
            {ck_yn && (
              <div className='flex items-center gap-2'>
                <select
                  name='loai_ck'
                  value={state.loai_ck}
                  disabled={isViewMode}
                  onChange={event => {
                    setValue('loai_ck', event.target.value);
                    actions.setLoaiCk(event.target.value);
                  }}
                  className={`${topSelectClassName} w-[250px]`}
                >
                  <option value='1'>Tự nhập</option>
                  <option value='2'>Giảm % theo hoá đơn</option>
                  <option value='3'>Giảm tiền trên tổng hóa đơn</option>
                </select>
                {String(loai_ck) !== '1' && (
                  <NumericInputV2
                    name='ck_tl_nt'
                    disabled={isViewMode}
                    value={state.ck_tl_nt}
                    onValueChange={nextValue => {
                      setValue('ck_tl_nt', nextValue);
                      actions.setCkTlNt(nextValue);
                    }}
                    className={`${topNumberInputClassName} w-[150px]`}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className='grid grid-cols-12 gap-4 xl:gap-6'>
          <div className='col-span-6 min-w-0 space-y-1'>
            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Mã nhà cung cấp</Label>
              <div className='flex min-w-0 flex-1 items-center gap-3'>
                <div className='w-[180px] xl:w-[200px]'>
                  <SearchField<KhachHang>
                    type='text'
                    name='ma_kh'
                    value={state.khachHang?.customer_code || state.khachHang?.customer_number || ''}
                    searchEndpoint={`/${QUERY_KEYS.NHA_CUNG_CAP}/`}
                    searchColumns={khachHangSearchColumns}
                    onRowSelection={(row: KhachHang) => {
                      actions.setKhachHang(row);
                      setValue('ma_kh', row?.uuid || '', { shouldDirty: true, shouldValidate: true });
                    }}
                    columnDisplay='customer_code'
                    dialogTitle='Danh mục đối tượng'
                    disabled={isViewMode}
                    className='w-full'
                  />
                </div>

                <div className='flex min-w-0 flex-1 items-center gap-2'>
                  <Label className='w-20 shrink-0 text-[13px]'>Mã số thuế</Label>
                  <div className='min-w-0 max-w-[220px] flex-1'>
                    <FormFieldV2
                      name='ma_so_thue'
                      type='text'
                      placeholder='Nhập và tra cứu'
                      value={state.khachHang?.tax_code || ''}
                      disabled={isViewMode}
                    />
                  </div>
                  <button type='button' className='flex items-center justify-center border border-gray-200 px-3 py-1.5'>
                    <AritoIcon icon={15} className='shrink-0' />
                  </button>
                  <button type='button' className='flex items-center justify-center border border-gray-200 px-3 py-1.5'>
                    <AritoIcon icon={888} />
                  </button>
                </div>
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Tên nhà cung cấp</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2
                  name='ten_kh'
                  type='text'
                  value={state.khachHang?.customer_name || ''}
                  disabled={isViewMode}
                />
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Địa chỉ</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2 name='dia_chi' type='text' value={state.khachHang?.address || ''} disabled={isViewMode} />
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Mã nhân viên</Label>
              <SearchField<NhanVien>
                type='text'
                name='ma_nvmh'
                value={state.nhanVien?.ma_nhan_vien || ''}
                relatedFieldValue={state.nhanVien?.ho_ten_nhan_vien || ''}
                searchEndpoint={`/${QUERY_KEYS.NHAN_VIEN}/`}
                columnDisplay='ma_nhan_vien'
                displayRelatedField='ho_ten_nhan_vien'
                searchColumns={nhanVienSearchColumns}
                dialogTitle='Danh sách nhân viên mua hàng'
                onRowSelection={actions.setNhanVien}
                disabled={isViewMode}
                className='w-100'
              />
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Tài khoản có</Label>
              <SearchField<AccountModel>
                value={state.taiKhoan?.code || state.khachHang?.account_data?.code || ''}
                relatedFieldValue={state.taiKhoan?.name || state.khachHang?.account_data?.name || ''}
                searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
                columnDisplay='code'
                displayRelatedField='name'
                searchColumns={accountSearchColumns}
                dialogTitle='Danh sách tài khoản'
                onRowSelection={handleAccountSelection}
                disabled={isViewMode}
                className='w-100'
              />
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Diễn giải</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2 name='dien_giai' type='text' disabled={isViewMode} />
              </div>
            </div>
          </div>

          <div className='col-span-3 min-w-0 space-y-1'>
            <div className={rowClassName}>
              <FormFieldV2
                name='du_cn_thu'
                label='Dư công nợ'
                labelClassName={middleLabelClassName}
                fieldClassName='w-full text-red-600'
                className='w-[220px]'
                disabled
              />
            </div>

            <div className={rowClassName}>
              <FormFieldV2
                name='ong_ba'
                label='Người giao hàng'
                labelClassName={middleLabelClassName}
                fieldClassName='w-full'
                className='w-[220px]'
                value={state.khachHang?.contact_person || ''}
                disabled={isViewMode}
              />
            </div>

            <div className={rowClassName}>
              <FormFieldV2
                name='e_mail'
                label='Email'
                labelClassName={middleLabelClassName}
                fieldClassName='w-full'
                className='w-[220px]'
                value={state.khachHang?.email || ''}
                disabled={isViewMode}
              />
            </div>

            <div className={rowClassName}>
              <Label className={middleLabelClassName}>Hạn thanh toán</Label>
              <div className='w-[220px]'>
                <SearchField<HanThanhToan>
                  type='text'
                  name='ma_tt'
                  value={state.hanThanhToan?.ma_tt || state.khachHang?.payment_term_data?.ma_tt || ''}
                  searchEndpoint={`/${QUERY_KEYS.HAN_THANH_TOAN}/`}
                  columnDisplay='ma_tt'
                  searchColumns={hanThanhToanSearchColumns}
                  dialogTitle='Danh mục hạn thanh toán'
                  onRowSelection={actions.setHanThanhToan}
                  className='w-full'
                  disabled={isViewMode}
                />
              </div>
            </div>
          </div>

          <div className='col-span-3 flex min-w-0 items-stretch'>
            <div className='min-w-0 flex-1 space-y-2 pr-3'>
              {selectedTabId === 0 && (
                <div
                  role='tabpanel'
                  id='hoa-don-mua-trong-nuoc-mini-panel-0'
                  aria-labelledby='hoa-don-mua-trong-nuoc-mini-tab-0'
                >
                  <DocumentNumberFieldV2
                    ma_ct={MA_CHUNG_TU.MUA_HANG.HOA_DON_MUA_HANG_TRONG_NUOC}
                    quyenChungTu={state.quyenChungTu}
                    onQuyenChungTuChange={actions.setQuyenChungTu}
                    soChungTu={state.soChungTu || ''}
                    onSoChungTuChange={actions.setSoChungTu}
                    disabled={isViewMode}
                    className='flex items-center gap-3'
                    labelClassName={rightLabelClassName}
                    classNameSearchField='min-w-0 flex-1'
                    formMode={formMode}
                  />

                  <FormFieldV2
                    label='Ngày chứng từ'
                    name='ngay_ct'
                    type='date'
                    labelClassName={rightLabelClassName}
                    className='w-full'
                    disabled={isViewMode}
                  />
                </div>
              )}

              {selectedTabId === 1 && (
                <div
                  role='tabpanel'
                  id='hoa-don-mua-trong-nuoc-mini-panel-1'
                  aria-labelledby='hoa-don-mua-trong-nuoc-mini-tab-1'
                >
                  <FormFieldV2
                    label='Số hóa đơn'
                    name='so_ct0'
                    disabled={isViewMode}
                    className='w-full'
                    labelClassName={rightLabelClassName}
                  />
                  <FormFieldV2
                    label='Ngày hóa đơn'
                    name='ngay_ct0'
                    type='date'
                    labelClassName={rightLabelClassName}
                    className='w-full'
                    disabled={isViewMode}
                  />
                  <FormFieldV2
                    label='Ký hiệu'
                    name='so_ct2'
                    type='text'
                    labelClassName={rightLabelClassName}
                    className='w-full'
                    disabled={isViewMode}
                  />
                </div>
              )}

              {pn_yn && selectedTabId === 2 && (
                <div
                  role='tabpanel'
                  id='hoa-don-mua-trong-nuoc-mini-panel-2'
                  aria-labelledby='hoa-don-mua-trong-nuoc-mini-tab-2'
                >
                  <DocumentNumberFieldV2
                    ma_ct={MA_CHUNG_TU.TON_KHO.PHIEU_NHAP_KHO}
                    quyenChungTu={state.quyenPhieuNhap}
                    onQuyenChungTuChange={actions.setQuyenPhieuNhap}
                    soChungTu={state.soPhieuNhap || ''}
                    onSoChungTuChange={actions.setSoPhieuNhap}
                    dateFieldName='ngay_pn'
                    disabled={isViewMode}
                    className='flex items-center gap-3'
                    labelClassName={rightLabelClassName}
                    classNameSearchField='min-w-0 flex-1'
                    label='Số ph/nhập'
                    formMode={formMode}
                  />

                  <FormFieldV2
                    label='Ngày nhập kho'
                    name='ngay_pn'
                    type='date'
                    labelClassName={rightLabelClassName}
                    className='w-full'
                    disabled={isViewMode}
                  />
                </div>
              )}

              <CurrencyInputV2
                formMode={formMode}
                className='flex items-center gap-3'
                labelClassName={rightLabelClassName}
                classNameInput='min-w-0 flex-1'
              />

              <FormFieldV2
                name='status'
                type='select'
                label='Trạng thái'
                labelClassName={rightLabelClassName}
                fieldClassName='w-full'
                className='w-full'
                options={[
                  { value: '0', label: 'Lập chứng từ' },
                  { value: '3', label: 'Chờ duyệt' },
                  { value: '5', label: 'Xuất hóa đơn' }
                ]}
                disabled={isViewMode}
              />

              <div className='mt-1 flex'>
                <div className='h-2 w-[104px] shrink-0 xl:w-[112px] 2xl:w-[127px]' />
                <FormFieldV2
                  label='Dữ liệu được nhận'
                  name='transfer_yn'
                  type='checkbox'
                  disabled
                  labelClassName='w-32 text-[13px]'
                  value={false}
                />
              </div>
            </div>

            <div
              role='tablist'
              aria-orientation='vertical'
              className='-mt-7 flex w-10 shrink-0 flex-col items-end gap-1 self-start'
            >
              {availableTabs.map(tab => (
                <button
                  key={tab.id}
                  type='button'
                  role='tab'
                  id={`hoa-don-mua-trong-nuoc-mini-tab-${tab.id}`}
                  aria-selected={selectedTabId === tab.id}
                  aria-controls={`hoa-don-mua-trong-nuoc-mini-panel-${tab.id}`}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    setSelectedTabId(tab.id);
                  }}
                  className={`flex min-h-[64px] w-5 items-center justify-center rounded-sm px-1 py-2 text-center text-[10px] font-bold text-white outline-none [writing-mode:vertical-rl] ${
                    selectedTabId === tab.id ? 'bg-main/80' : 'bg-gray-500 hover:bg-gray-600'
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
